// src/components/AppLayout/AppLayout.tsx
import { AppShell, Burger, Group, Skeleton, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { jsx, jsxs } from "react/jsx-runtime";
function AppLayout({
  children
}) {
  const [opened, { toggle }] = useDisclosure();
  return /* @__PURE__ */ jsxs(
    AppShell,
    {
      header: { height: 60 },
      navbar: { width: 300, breakpoint: "sm", collapsed: { mobile: !opened } },
      padding: "md",
      children: [
        /* @__PURE__ */ jsx(AppShell.Header, { children: /* @__PURE__ */ jsxs(Group, { h: "100%", px: "md", children: [
          /* @__PURE__ */ jsx(Burger, { opened, onClick: toggle, hiddenFrom: "sm", size: "sm" }),
          /* @__PURE__ */ jsx(Text, { children: "Luminarium App" }),
          " "
        ] }) }),
        /* @__PURE__ */ jsxs(AppShell.Navbar, { p: "md", children: [
          "Navbar",
          /* @__PURE__ */ jsx(Skeleton, { height: 8, radius: "xl" }),
          /* @__PURE__ */ jsx(Skeleton, { height: 8, mt: 6, radius: "xl" }),
          /* @__PURE__ */ jsx(Skeleton, { height: 8, mt: 6, width: "70%", radius: "xl" })
        ] }),
        /* @__PURE__ */ jsx(AppShell.Main, { children })
      ]
    }
  );
}

// src/components/CompactProductLockup/CompactProductLockup.tsx
import { Box, Text as Text2, useMantineTheme } from "@mantine/core";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var BADGE_COLOUR = "#99563F";
var BADGE_TINT = "rgba(153, 86, 63, 0.12)";
function CompactProductLockup({
  ground,
  productName = "Discovery Dialogues",
  badge
}) {
  const theme2 = useMantineTheme();
  return /* @__PURE__ */ jsxs2(Box, { style: { display: "inline-flex", alignItems: "center", gap: 11 }, children: [
    /* @__PURE__ */ jsx2(
      "svg",
      {
        "aria-hidden": "true",
        focusable: "false",
        viewBox: "0 0 518.04 682.207",
        style: { height: 21, width: "auto", flexShrink: 0 },
        children: /* @__PURE__ */ jsx2(
          "polygon",
          {
            fill: "#EE6100",
            points: "337.624,310.292 517.04,281.789 517.04,250.189 337.624,221.064 422.284,16.8 408.601,8.9 394.918,1 260.35,176.45 145.419,35.634 118.053,51.434 183.076,221.064 1,252.763 1,284.363 183.076,310.292 11.323,665.407 25.006,673.307 38.688,681.207 260.35,354.906 373.843,499.624 401.21,483.824"
          }
        )
      }
    ),
    /* @__PURE__ */ jsx2(
      Text2,
      {
        span: true,
        ff: "heading",
        fz: 16,
        c: ground === "dark" ? theme2.other.ivory : "navy.8",
        style: { whiteSpace: "nowrap" },
        children: productName
      }
    ),
    badge === void 0 ? null : /* @__PURE__ */ jsx2(
      Text2,
      {
        span: true,
        ff: "Inter, sans-serif",
        fw: 600,
        fz: 10.5,
        tt: "uppercase",
        style: {
          color: BADGE_COLOUR,
          backgroundColor: BADGE_TINT,
          borderRadius: 4,
          padding: "2px 7px",
          letterSpacing: "0.08em",
          lineHeight: 1.4,
          whiteSpace: "nowrap"
        },
        children: badge
      }
    )
  ] });
}

// src/theme/theme.ts
import { createTheme, NavLink, Table } from "@mantine/core";
var HAIRLINE = "rgba(29, 36, 50, 0.22)";
var IVORY = "#FAF4E8";
var theme = createTheme({
  fontFamily: "'Inter', Aptos, 'Segoe UI', Arial, sans-serif",
  fontFamilyMonospace: "Monaco, Courier, monospace",
  headings: {
    fontFamily: "'Bespoke Serif', Aptos, Georgia, serif",
    fontWeight: "400",
    sizes: {
      h1: { fontSize: "2.5rem", lineHeight: "1.2" },
      h2: { fontSize: "2rem", lineHeight: "1.3" },
      h3: { fontSize: "1.5rem", lineHeight: "1.4" },
      h4: { fontSize: "1.25rem", lineHeight: "1.4" },
      h5: { fontSize: "1.125rem", lineHeight: "1.5" },
      h6: { fontSize: "1rem", lineHeight: "1.5" }
    }
  },
  white: "#FFFFFF",
  black: "#1D2432",
  // Primary actions are structural dark navy (matches the website's CTA:
  // navy fill, ivory text). Orange is the brand *signal* — roughly one
  // emphasis moment per view (the star, a key highlight) — so it is never
  // the default button colour; opt in with color="orange" only where a
  // view earns its single signal, and never as a fill on ivory/white.
  primaryColor: "navy",
  primaryShade: 8,
  colors: {
    orange: [
      "#FFF0E4",
      "#FFDFC7",
      "#FFC59E",
      "#FCA76D",
      "#F98B3E",
      "#F37517",
      "#EE6100",
      "#C85200",
      "#9C4000",
      "#642300"
    ],
    navy: [
      "#F0F3F7",
      "#E0E6EE",
      "#C9D3E1",
      "#A3B5D1",
      "#8095B4",
      "#5F7695",
      "#435874",
      "#2D3A4F",
      "#1D2432",
      "#141516"
    ],
    green: [
      "#F4F8F4",
      "#E8F1E7",
      "#D5E5D1",
      "#BCD5B6",
      "#99BF90",
      "#69A15C",
      "#4E7F43",
      "#406438",
      "#344F2F",
      "#2A3D27"
    ],
    red: [
      "#FEF3F1",
      "#FEE5E0",
      "#FCCFC5",
      "#FAB1A2",
      "#F88973",
      "#F55231",
      "#BD4026",
      "#933522",
      "#712D1F",
      "#54251C"
    ],
    yellow: [
      "#FEF8F1",
      "#FCF0E1",
      "#FAE4C6",
      "#F7D3A4",
      "#F3BC75",
      "#ED9D34",
      "#B87514",
      "#8F5D14",
      "#6E4A15",
      "#523915"
    ],
    blue: [
      "#F4F6FB",
      "#E7EDF7",
      "#D2DDF0",
      "#B7C8E8",
      "#93ACDC",
      "#6085CB",
      "#4E71BA",
      "#405A91",
      "#344870",
      "#2A3854"
    ],
    violet: [
      "#FAF3F7",
      "#F4E5EE",
      "#EBCFDF",
      "#DFB2CB",
      "#CF8AB0",
      "#B8538B",
      "#A34479",
      "#7F3860",
      "#632F4C",
      "#4A273C"
    ],
    teal: [
      "#F4F7F6",
      "#E7EFEC",
      "#D2E1DB",
      "#B7CEC5",
      "#92B6A8",
      "#5E937F",
      "#3E8574",
      "#34695C",
      "#2B534A",
      "#24403A"
    ]
  },
  other: {
    // Brand foundation background (--lum-ivory); canvas behind white cards.
    ivory: IVORY,
    // Structural rule colour for borders on white/ivory surfaces.
    hairline: HAIRLINE
  },
  // Spacing scale based on 6pt unit from brand guidelines.
  spacing: {
    xs: "0.375rem",
    // 6px
    sm: "0.75rem",
    // 12px
    md: "1.125rem",
    // 18px
    lg: "1.5rem",
    // 24px
    xl: "2.25rem"
    // 36px
  },
  defaultRadius: "md",
  components: {
    Button: {
      defaultProps: {
        size: "md",
        variant: "filled"
      }
    },
    // Nav stays structural (navy) so orange remains the one signal per view.
    NavLink: NavLink.extend({
      defaultProps: { color: "navy" }
    }),
    // Design-system table treatment: white card on the ivory canvas with a
    // hairline border, dark-navy header band with ivory small-caps labels.
    Table: Table.extend({
      // Row-divider colour must go through Mantine's vars resolver — it
      // injects --table-border-color itself, so a styles override loses.
      vars: () => ({
        table: { "--table-border-color": HAIRLINE }
      }),
      styles: {
        table: {
          backgroundColor: "var(--mantine-color-white)",
          border: `1px solid ${HAIRLINE}`
        },
        thead: {
          backgroundColor: "var(--mantine-color-navy-8)"
        },
        th: {
          color: IVORY,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em"
        }
      }
    })
  }
});
export {
  AppLayout,
  CompactProductLockup,
  theme
};
